// frontend/src/hooks/useErrorHandling.ts
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  classifyError,
  logError,
  getErrorMessage,
  type AppError,
} from '../utils/errorHandling';
import { networkMonitor } from '../services/apiService';

export interface ErrorState {
  error: AppError | null;
  isLoading: boolean;
  retryCount: number;
  lastRetryAt: Date | null;
}

export interface ErrorHandlingOptions {
  showUserFeedback?: boolean;
  logErrors?: boolean;
  maxRetries?: number;
  retryDelay?: number; // base delay (ms) for retries
  onError?: (error: AppError) => void;
  onRetry?: (retryCount: number) => void;
  onSuccess?: () => void;
}

// Defaults
const DEFAULT_OPTIONS: Required<ErrorHandlingOptions> = {
  showUserFeedback: true,
  logErrors: true,
  maxRetries: 3,
  retryDelay: 1000, // 1s base
  onError: () => {},
  onRetry: () => {},
  onSuccess: () => {},
};

export const useErrorHandling = (options: ErrorHandlingOptions = {}) => {
  // Stabilize options to avoid dependency churn in callbacks/effects
  const finalOptions = useMemo(
    () => ({ ...DEFAULT_OPTIONS, ...options }),
    [options]
  );

  const [errorState, setErrorState] = useState<ErrorState>({
    error: null,
    isLoading: false,
    retryCount: 0,
    lastRetryAt: null,
  });

  const [networkStatus, setNetworkStatus] = useState(
    networkMonitor.getCurrentStatus()
  );

  // Timer refs
  const retryTimeoutRef = useRef<number | null>(null);

  // Reentry/backoff guard to suppress overlapping retries
  const inBackoffRef = useRef(false);

  // Mounted flag to avoid setState after unmount
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const unsubscribe = networkMonitor.addListener((online) => {
      if (!mountedRef.current) return;
      setNetworkStatus(online);
    });
    return () => {
      mountedRef.current = false;
      unsubscribe?.();
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);

  const safeSetState = useCallback(<T,>(updater: (prev: T) => T) => {
    if (!mountedRef.current) return;
    // @ts-expect-error generic helper
    setErrorState((prev) => updater(prev));
  }, []);

  const clearError = useCallback(() => {
    safeSetState<ErrorState>((prev) => ({
      ...prev,
      error: null,
      retryCount: 0,
      lastRetryAt: null,
    }));
  }, [safeSetState]);

  const setLoading = useCallback(
    (loading: boolean) => {
      safeSetState<ErrorState>((prev) => ({
        ...prev,
        isLoading: loading,
      }));
    },
    [safeSetState]
  );

  const handleError = useCallback(
    (error: unknown, context?: string) => {
      const appError = classifyError(error);
      console.log('handleError called, setting error:', appError.userMessage);

      if (finalOptions.logErrors) {
        logError(appError, context);
      }

      safeSetState<ErrorState>((prev) => ({
        ...prev,
        error: appError,
        isLoading: false,
      }));

      finalOptions.onError(appError);
      return appError;
    },
    [finalOptions, safeSetState]
  );

  const executeWithErrorHandling = useCallback(
    async <T,>(operation: () => Promise<T>, context?: string): Promise<T | null> => {
      setLoading(true);
      // Only clear previous error just before executing to avoid loops when
      // consumers include error in their effect deps (discouraged).
      clearError();

      try {
        const result = await operation();
        setLoading(false);
        finalOptions.onSuccess();
        return result;
      } catch (e) {
        handleError(e, context);
        return null;
      }
    },
    [clearError, handleError, setLoading, finalOptions]
  );

  // Exponential backoff retry with guard
  const retry = useCallback(
    async <T,>(operation: () => Promise<T>) => {
      // Prevent overlapping retries during backoff window
      if (inBackoffRef.current) {
        return;
      }

      // Snapshot for decision making
      const currentRetry = errorState.retryCount;

      if (currentRetry >= finalOptions.maxRetries) {
        return;
      }

      const nextCount = currentRetry + 1;

      safeSetState<ErrorState>((prev) => ({
        ...prev,
        retryCount: nextCount,
        lastRetryAt: new Date(),
        isLoading: true,
      }));

      finalOptions.onRetry(nextCount);

      // Backoff: delay = base * 2^(attempt-1)
      const delay = finalOptions.retryDelay * Math.pow(2, nextCount - 1);

      inBackoffRef.current = true;
      await new Promise<void>((resolve) => {
        retryTimeoutRef.current = window.setTimeout(() => {
          resolve();
        }, delay);
      });
      inBackoffRef.current = false;

      try {
        const result = await operation();
        safeSetState<ErrorState>((prev) => ({
          ...prev,
          error: null,
          isLoading: false,
        }));
        finalOptions.onSuccess();
        return result;
      } catch (e) {
        handleError(e, 'Retry attempt failed');
        throw e;
      }
    },
    [errorState.retryCount, finalOptions, handleError, safeSetState]
  );

  // Derived/computed
  const canRetry =
    (errorState.error?.retryable ?? true) &&
    errorState.retryCount < finalOptions.maxRetries;

  const shouldShowNetworkWarning =
    !networkStatus && errorState.error?.type === 'network';

  return {
    // State
    error: errorState.error,
    isLoading: errorState.isLoading,
    retryCount: errorState.retryCount,
    lastRetryAt: errorState.lastRetryAt,
    networkStatus,

    // Computed
    canRetry,
    shouldShowNetworkWarning,
    errorMessage: errorState.error ? getErrorMessage(errorState.error) : null,

    // Actions
    clearError,
    setLoading,
    handleError,
    retry,
    executeWithErrorHandling,
  };
};

// Hook for form validation errors
export const useValidationErrors = () => {
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const setFieldError = useCallback((field: string, message: string) => {
    setValidationErrors((prev) => ({
      ...prev,
      [field]: message,
    }));
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setValidationErrors({});
  }, []);

  const hasErrors = Object.keys(validationErrors).length > 0;
  const getFieldError = useCallback(
    (field: string) => validationErrors[field],
    [validationErrors]
  );

  return {
    validationErrors,
    hasErrors,
    setFieldError,
    clearFieldError,
    clearAllErrors,
    getFieldError,
  };
};

// Hook for network-aware operations
export const useNetworkAwareOperation = () => {
  const [isOnline, setIsOnline] = useState(networkMonitor.getCurrentStatus());
  const [queuedOperations, setQueuedOperations] = useState<(() => Promise<any>)[]>([]);

  useEffect(() => {
    const unsubscribe = networkMonitor.addListener(async (online) => {
      setIsOnline(online);

      // Drain queue on reconnect
      if (online && queuedOperations.length > 0) {
        const ops = [...queuedOperations];
        setQueuedOperations([]);
        for (const op of ops) {
          try {
            await op();
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to execute queued operation:', e);
          }
        }
      }
    });

    return unsubscribe;
  }, [queuedOperations]);

  const executeOrQueue = useCallback(
    async <T,>(
      operation: () => Promise<T>,
      options: { executeOffline?: boolean; queueWhenOffline?: boolean } = {}
    ): Promise<T | null> => {
      const { executeOffline = false, queueWhenOffline = true } = options;

      if (!isOnline && !executeOffline) {
        if (queueWhenOffline) {
          setQueuedOperations((prev) => [...prev, operation]);
          return null;
        }
        throw new Error('Operation requires network connection');
      }

      return await operation();
    },
    [isOnline]
  );

  return {
    isOnline,
    queuedOperationsCount: queuedOperations.length,
    executeOrQueue,
    clearQueue: () => setQueuedOperations([]),
  };
};
